<template>
    <div class="honor-board-list" :class="{ 'honor-board-list--padded': padded }">
        <header class="honor-board-list__header">
            <div class="honor-board-list__header-main">
                <div class="honor-board-list__badge" aria-hidden="true">
                    <v-icon color="white" size="22">mdi-trophy</v-icon>
                </div>
                <div>
                    <h1 class="honor-board-list__title">لوحة الشرف</h1>
                    <p v-if="examName" class="honor-board-list__exam">
                        امتحان: <strong>{{ examName }}</strong>
                    </p>
                    <p v-if="resolvedSubtitle" class="honor-board-list__subtitle">{{ resolvedSubtitle }}</p>
                </div>
            </div>
            <div class="honor-board-list__header-actions">
                <v-chip v-if="students.length" color="primary" outlined class="honor-board-list__count">
                    {{ countLabel || (students.length + ' طالب') }}
                </v-chip>
                <v-btn
                    v-if="viewAllTo"
                    text
                    color="primary"
                    class="px-2"
                    :to="viewAllTo"
                >
                    عرض الكل
                    <v-icon small class="ms-1">mdi-chevron-left</v-icon>
                </v-btn>
            </div>
        </header>

        <template v-if="students.length">
            <!-- Desktop podium -->
            <section
                v-if="showPodium && topStudents.length && !phoneOnly"
                class="honor-podium"
                aria-label="المراكز الأولى"
            >
                <article
                    v-for="student in podiumStudents"
                    :key="'podium-' + student.id + '-' + student.rank"
                    class="honor-podium__card"
                    :class="[
                        'honor-podium__card--' + student.rank,
                        { 'honor-podium__card--leader': student.rank === 1 },
                    ]"
                >
                    <div class="honor-podium__medal" :class="'honor-podium__medal--' + student.rank">
                        <v-icon v-if="student.rank === 1" size="18">mdi-crown</v-icon>
                        <span v-else>{{ student.rank }}</span>
                    </div>
                    <v-avatar
                        :size="student.rank === 1 ? 92 : 76"
                        class="honor-podium__avatar"
                        :class="'honor-podium__avatar--' + student.rank"
                    >
                        <img :src="student.avatar" :alt="student.name" />
                    </v-avatar>
                    <div class="honor-podium__name">{{ student.name }}</div>
                    <div v-if="student.governorate" class="honor-podium__gov">{{ student.governorate }}</div>
                    <div
                        v-if="student.earnedMark != null && student.totalMark != null"
                        class="honor-podium__score"
                    >
                        <span class="honor-podium__score-main ltr-dir">
                            {{ student.earnedMark }}/{{ student.totalMark }}
                        </span>
                        <span v-if="scorePercent(student) != null" class="honor-podium__score-pct">
                            {{ scorePercent(student) }}%
                        </span>
                    </div>
                    <div
                        v-if="student.finishTime || student.timeTaken != null"
                        class="honor-podium__meta"
                    >
                        <span v-if="student.finishTime" class="ltr-dir">
                            {{ dateTimeFormat(student.finishTime, isTimestamp(student.finishTime)) }}
                        </span>
                        <span v-if="student.timeTaken != null">
                            {{ formatSeconds(student.timeTaken) }}
                        </span>
                    </div>
                    <div class="honor-podium__label">{{ podiumLabel(student.rank) }}</div>
                </article>
            </section>

            <!-- Mobile: compact detail rows for top 3 so more ranks fit on screen -->
            <section
                v-if="showPodium && topStudents.length && phoneOnly"
                class="honor-top-mobile"
                aria-label="المراكز الأولى"
            >
                <article
                    v-for="student in topStudentsSorted"
                    :key="'top-m-' + student.id + '-' + student.rank"
                    class="honor-top-mobile__row"
                    :class="'honor-top-mobile__row--' + student.rank"
                >
                    <div class="honor-top-mobile__rank honor-rank" :class="rankClass(student.rank)">
                        <v-icon v-if="student.rank === 1" size="14">mdi-crown</v-icon>
                        <span v-else>{{ student.rank }}</span>
                    </div>
                    <v-avatar size="48" class="honor-top-mobile__avatar flex-shrink-0">
                        <img :src="student.avatar" :alt="student.name" />
                    </v-avatar>
                    <div class="honor-top-mobile__body">
                        <div class="honor-top-mobile__name">{{ student.name }}</div>
                        <div class="honor-top-mobile__details">
                            <span v-if="student.governorate" class="honor-top-mobile__gov">{{ student.governorate }}</span>
                            <span v-if="student.governorate && student.earnedMark != null" class="honor-top-mobile__dot">·</span>
                            <span
                                v-if="student.earnedMark != null && student.totalMark != null"
                                class="honor-top-mobile__score ltr-dir"
                            >
                                {{ student.earnedMark }}/{{ student.totalMark }}
                            </span>
                            <span v-if="scorePercent(student) != null" class="honor-top-mobile__pct">
                                ({{ scorePercent(student) }}%)
                            </span>
                        </div>
                        <div class="honor-top-mobile__place">{{ podiumLabel(student.rank) }}</div>
                    </div>
                </article>
            </section>

            <!-- Remaining ranks / full list when podium off -->
            <section v-if="listStudents.length" class="honor-board-list__rest">
                <div v-if="showPodium && topStudents.length" class="honor-board-list__rest-title">
                    باقي الترتيب
                </div>

                <v-card v-if="pcOnly" flat class="bordered honor-board-list__table-card">
                    <v-simple-table class="honor-board-list__table">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 72px">الترتيب</th>
                                <th>الطالب</th>
                                <th v-if="hasGovernorate">المحافظة</th>
                                <th v-if="hasScores" class="text-center">الدرجة</th>
                                <th v-if="hasFinishTime">وقت إنهاء الامتحان</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="student in listStudents"
                                :key="student.id + '-' + student.rank"
                                class="honor-board-list__row"
                            >
                                <td class="text-center">
                                    <span class="honor-rank" :class="rankClass(student.rank)">{{ student.rank }}</span>
                                </td>
                                <td>
                                    <div class="d-flex align-center">
                                        <v-avatar size="44" class="me-3">
                                            <img :src="student.avatar" :alt="student.name" />
                                        </v-avatar>
                                        <div>
                                            <div class="font-weight-bold strong-text--text">{{ student.name }}</div>
                                            <div
                                                v-if="scorePercent(student) != null"
                                                class="details-text muted--text"
                                            >
                                                نسبة النجاح {{ scorePercent(student) }}%
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td v-if="hasGovernorate" class="text--text">{{ student.governorate }}</td>
                                <td v-if="hasScores" class="text-center">
                                    <v-chip
                                        v-if="student.earnedMark != null && student.totalMark != null"
                                        small
                                        color="primary"
                                        class="font-weight-bold ltr-dir"
                                    >
                                        {{ student.earnedMark }}/{{ student.totalMark }}
                                    </v-chip>
                                </td>
                                <td v-if="hasFinishTime">
                                    <div
                                        v-if="student.finishTime || student.timeTaken != null"
                                        class="d-flex flex-column"
                                    >
                                        <span v-if="student.finishTime" class="ltr-dir">
                                            {{ dateTimeFormat(student.finishTime, isTimestamp(student.finishTime)) }}
                                        </span>
                                        <span v-if="student.timeTaken != null" class="details-text muted--text">
                                            الوقت المستغرق: {{ formatSeconds(student.timeTaken) }}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>

                <div v-else class="honor-board-list__mobile">
                    <article
                        v-for="student in listStudents"
                        :key="'m-' + student.id + '-' + student.rank"
                        class="honor-board-list__mobile-row"
                    >
                        <div class="honor-rank me-3" :class="rankClass(student.rank)">
                            {{ student.rank }}
                        </div>
                        <v-avatar size="52" class="me-3 flex-shrink-0">
                            <img :src="student.avatar" :alt="student.name" />
                        </v-avatar>
                        <div class="honor-board-list__mobile-body">
                            <div class="font-weight-bold strong-text--text">{{ student.name }}</div>
                            <div class="honor-board-list__mobile-meta">
                                <v-chip
                                    v-if="student.earnedMark != null && student.totalMark != null"
                                    label
                                    x-small
                                    color="primary"
                                    class="ltr-dir px-2 me-2 font-weight-bold"
                                >
                                    {{ student.earnedMark }}/{{ student.totalMark }}
                                </v-chip>
                                <span v-if="scorePercent(student) != null" class="details-text muted--text me-2">
                                    {{ scorePercent(student) }}%
                                </span>
                                <span v-if="student.governorate" class="details-text text--text">
                                    {{ student.governorate }}
                                </span>
                            </div>
                            <div
                                v-if="student.finishTime || student.timeTaken != null"
                                class="details-text muted--text mt-1"
                            >
                                <span v-if="student.finishTime" class="ltr-dir me-2">
                                    {{ dateTimeFormat(student.finishTime, isTimestamp(student.finishTime)) }}
                                </span>
                                <span v-if="student.timeTaken != null">
                                    ({{ formatSeconds(student.timeTaken) }})
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

export default {
    name: 'HonorBoardList',
    props: {
        students: {
            type: Array,
            default: () => [],
        },
        examName: {
            type: String,
            default: null,
        },
        subtitle: {
            type: String,
            default: null,
        },
        countLabel: {
            type: String,
            default: null,
        },
        viewAllTo: {
            type: [Object, String],
            default: null,
        },
        padded: {
            type: Boolean,
            default: false,
        },
        highlightTop: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        showPodium() {
            return this.highlightTop && this.students.length >= 1;
        },
        topStudents() {
            return this.students.filter((student) => student.rank >= 1 && student.rank <= 3);
        },
        topStudentsSorted() {
            return [...this.topStudents].sort((a, b) => a.rank - b.rank);
        },
        // Visual order for RTL podium: 2 | 1 | 3
        podiumStudents() {
            const byRank = {};
            this.topStudents.forEach((student) => {
                byRank[student.rank] = student;
            });
            return [byRank[2], byRank[1], byRank[3]].filter(Boolean);
        },
        listStudents() {
            if (!this.showPodium) {
                return this.students;
            }
            return this.students.filter((student) => student.rank > 3);
        },
        hasFinishTime() {
            return this.students.some((student) => student.finishTime || student.timeTaken != null);
        },
        hasGovernorate() {
            return this.students.some((student) => !!student.governorate);
        },
        hasScores() {
            return this.students.some(
                (student) => student.earnedMark != null && student.totalMark != null
            );
        },
        resolvedSubtitle() {
            if (this.subtitle) {
                return this.subtitle;
            }
            if (this.hasFinishTime) {
                return 'الترتيب حسب درجة الامتحان، وعند التعادل يُفضَّل الأسرع إن وُجد وقت التسليم';
            }
            return 'الترتيب حسب درجة الامتحان';
        },
        ...GlobalComputed,
    },
    methods: {
        ...GlobalMethods,
        podiumLabel(rank) {
            if (rank === 1) return 'المركز الأول';
            if (rank === 2) return 'المركز الثاني';
            if (rank === 3) return 'المركز الثالث';
            return '';
        },
        scorePercent(student) {
            if (student.scorePercentage != null && Number.isFinite(Number(student.scorePercentage))) {
                return Math.round(Number(student.scorePercentage));
            }
            if (student.earnedMark != null && student.totalMark) {
                return Math.round((Number(student.earnedMark) / Number(student.totalMark)) * 100);
            }
            return null;
        },
        rankClass(rank) {
            if (rank === 1) return 'honor-rank--gold';
            if (rank === 2) return 'honor-rank--silver';
            if (rank === 3) return 'honor-rank--bronze';
            return 'honor-rank--default';
        },
        isTimestamp(value) {
            return typeof value === 'number' || /^\d+$/.test(String(value));
        },
        formatSeconds(seconds) {
            let remaining = Number(seconds) * 1000;
            if (!remaining) {
                return '00:00';
            }
            const hours = Math.floor(remaining / (1000 * 60 * 60));
            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
            const secs = Math.floor((remaining % (1000 * 60)) / 1000);
            const hoursText = hours < 10 ? `0${hours}` : hours;
            const minutesText = minutes < 10 ? `0${minutes}` : minutes;
            const secondsText = secs < 10 ? `0${secs}` : secs;
            return hours ? `${hoursText}:${minutesText}:${secondsText}` : `${minutesText}:${secondsText}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.honor-board-list--padded {
    padding-inline: 1rem;
}

.honor-board-list__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.25rem;
}

.honor-board-list__header-main {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    min-width: 0;
}

.honor-board-list__badge {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(145deg, #e53935, #b71c1c);
    box-shadow: 0 10px 22px rgba(229, 57, 53, 0.28);
}

.honor-board-list__title {
    margin: 0;
    font-size: clamp(1.35rem, 3vw, 1.7rem);
    font-weight: 900;
    line-height: 1.35;
    color: inherit;
}

.honor-board-list__exam {
    margin: 0.35rem 0 0;
    font-size: 0.95rem;
    line-height: 1.5;
}

.honor-board-list__subtitle {
    margin: 0.25rem 0 0;
    font-size: 0.88rem;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.55;
}

.honor-board-list__header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.honor-board-list__count {
    font-weight: 700 !important;
}

.honor-podium {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    align-items: end;
    margin-bottom: 1.5rem;
    padding: 1rem 0.5rem 0.25rem;
    border-radius: 1.1rem;
    background:
        radial-gradient(circle at 50% 0%, rgba(229, 57, 53, 0.12), transparent 55%),
        linear-gradient(180deg, #faf7f6 0%, #fff 100%);
    border: 1px solid rgba(17, 17, 17, 0.06);
}

@media (max-width: 700px) {
    .honor-board-list__header {
        margin-bottom: 0.65rem;
        gap: 0.45rem;
    }

    .honor-board-list__badge {
        width: 2.15rem;
        height: 2.15rem;
        border-radius: 0.7rem;
    }

    .honor-board-list__title {
        font-size: 1.15rem;
    }

    .honor-board-list__exam {
        font-size: 0.8rem;
        margin-top: 0.2rem;
    }

    .honor-board-list__subtitle {
        display: none;
    }

    .honor-board-list__rest-title {
        margin-bottom: 0.45rem;
        font-size: 0.9rem;
    }

    .honor-board-list__mobile-row {
        padding: 0.6rem 0.7rem;
    }
}

.honor-top-mobile {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.75rem;
    padding: 0.45rem;
    border-radius: 0.9rem;
    background:
        radial-gradient(circle at 100% 0%, rgba(229, 57, 53, 0.1), transparent 55%),
        linear-gradient(180deg, #faf7f6 0%, #fff 100%);
    border: 1px solid rgba(17, 17, 17, 0.06);
}

.honor-top-mobile__row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 4.1rem;
    padding: 0.45rem 0.55rem;
    border-radius: 0.75rem;
    background: #fff;
    border: 1px solid rgba(17, 17, 17, 0.07);
}

.honor-top-mobile__row--1 {
    border-color: rgba(229, 57, 53, 0.28);
    box-shadow: 0 6px 14px rgba(229, 57, 53, 0.1);
    background: linear-gradient(90deg, rgba(229, 57, 53, 0.05), #fff 55%);
}

.honor-top-mobile__row--2 {
    border-color: rgba(160, 160, 160, 0.35);
}

.honor-top-mobile__row--3 {
    border-color: rgba(198, 134, 66, 0.35);
}

.honor-top-mobile__rank.honor-rank {
    min-width: 1.7rem;
    height: 1.7rem;
    font-size: 0.75rem;
}

.honor-top-mobile__avatar {
    border: 2px solid rgba(0, 0, 0, 0.06);
}

.honor-top-mobile__row--1 .honor-top-mobile__avatar {
    border-color: rgba(229, 57, 53, 0.45);
}

.honor-top-mobile__body {
    min-width: 0;
    flex: 1;
}

.honor-top-mobile__name {
    font-size: 0.9rem;
    font-weight: 800;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.honor-top-mobile__details {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.15rem 0.3rem;
    margin-top: 0.15rem;
    font-size: 0.78rem;
    line-height: 1.35;
}

.honor-top-mobile__gov {
    color: rgba(0, 0, 0, 0.58);
}

.honor-top-mobile__dot {
    color: rgba(0, 0, 0, 0.35);
}

.honor-top-mobile__score {
    font-weight: 800;
    color: #c62828;
}

.honor-top-mobile__pct {
    color: rgba(0, 0, 0, 0.5);
    font-weight: 600;
}

.honor-top-mobile__place {
    margin-top: 0.1rem;
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(17, 17, 17, 0.55);
}

.honor-podium__card {
    position: relative;
    text-align: center;
    padding: 1.1rem 0.7rem 0.95rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid rgba(17, 17, 17, 0.08);
    box-shadow: 0 10px 24px rgba(17, 17, 17, 0.06);
    min-width: 0;
}

.honor-podium__card--leader {
    padding-top: 1.35rem;
    padding-bottom: 1.15rem;
    border-color: rgba(229, 57, 53, 0.28);
    box-shadow: 0 16px 32px rgba(229, 57, 53, 0.14);
    transform: translateY(-0.35rem);
}

.honor-podium__medal {
    position: absolute;
    top: 0.65rem;
    inset-inline-start: 0.65rem;
    min-width: 1.7rem;
    height: 1.7rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 800;
}

.honor-podium__medal--1 {
    background: linear-gradient(135deg, #f6d365, #fda085);
    color: #5b3b00;
}

.honor-podium__medal--2 {
    background: linear-gradient(135deg, #e8e8e8, #c0c0c0);
    color: #333;
}

.honor-podium__medal--3 {
    background: linear-gradient(135deg, #f0c27b, #c68642);
    color: #4a2c0a;
}

.honor-podium__avatar {
    border: 3px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 0.55rem;
}

.honor-podium__avatar--1 {
    border-color: rgba(229, 57, 53, 0.55);
}

.honor-podium__avatar--2 {
    border-color: rgba(160, 160, 160, 0.7);
}

.honor-podium__avatar--3 {
    border-color: rgba(198, 134, 66, 0.7);
}

.honor-podium__name {
    font-weight: 800;
    font-size: 0.98rem;
    line-height: 1.4;
    word-break: break-word;
}

.honor-podium__gov {
    margin-top: 0.2rem;
    font-size: 0.82rem;
    color: rgba(0, 0, 0, 0.58);
}

.honor-podium__score {
    margin-top: 0.65rem;
    display: inline-flex;
    align-items: baseline;
    gap: 0.4rem;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    background: rgba(229, 57, 53, 0.08);
    color: #c62828;
}

.honor-podium__score-main {
    font-weight: 900;
    font-size: 1rem;
}

.honor-podium__score-pct {
    font-size: 0.8rem;
    font-weight: 700;
    opacity: 0.85;
}

.honor-podium__meta {
    margin-top: 0.45rem;
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.55);
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.honor-podium__label {
    margin-top: 0.55rem;
    font-size: 0.78rem;
    font-weight: 800;
    color: rgba(17, 17, 17, 0.62);
}

.honor-board-list__rest-title {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    font-weight: 800;
}

.honor-board-list__table-card {
    overflow: hidden;
    border-radius: 1rem !important;
}

.honor-board-list__table {
    th {
        font-weight: 700 !important;
        white-space: nowrap;
    }
}

.honor-board-list__row:hover {
    background: rgba(229, 57, 53, 0.03);
}

.honor-board-list__mobile {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.honor-board-list__mobile-row {
    display: flex;
    align-items: center;
    padding: 0.75rem 0.85rem;
    border-radius: 0.9rem;
    background: #fff;
    border: 1px solid rgba(17, 17, 17, 0.07);
}

.honor-board-list__mobile-body {
    min-width: 0;
    flex: 1;
}

.honor-board-list__mobile-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 0.3rem;
    gap: 0.15rem 0;
}

.honor-rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 34px;
    padding: 0 8px;
    border-radius: 999px;
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1;
    flex-shrink: 0;

    &--gold {
        background: linear-gradient(135deg, #f6d365, #fda085);
        color: #5b3b00;
    }

    &--silver {
        background: linear-gradient(135deg, #e8e8e8, #c0c0c0);
        color: #333;
    }

    &--bronze {
        background: linear-gradient(135deg, #f0c27b, #c68642);
        color: #4a2c0a;
    }

    &--default {
        background: rgba(0, 0, 0, 0.06);
        color: inherit;
    }
}
</style>
